import * as React from 'react';

import { i18n } from 'i18n';
import {
  type DeviceFilter,
  DeviceSelectValues,
} from 'src/components/platforms/DeviceFamilyFilter/DeviceFamilyFilter.types';
import { usePrismUrlContext } from 'src/features/visibility/prism/contexts/PrismUrlContext';
import { useGetEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';

type CrossPlatformGlobalFilterContextType = {
  deviceFamilyFilters: { [key: string]: DeviceFilter };
  setDeviceFamilyFilters: (key: string, filter: DeviceFilter) => void;
  resetDeviceFamilyFilters: () => void;
  blueprintFilters: string[];
  setBlueprintFilters: (filters: string[]) => void;
  resetBlueprintFilters: () => void;
};

export const CrossPlatformGlobalFilterContext = React.createContext<
  CrossPlatformGlobalFilterContextType | undefined
>(undefined);

export const useCrossPlatformGlobalFilter = () => {
  const context = React.useContext(CrossPlatformGlobalFilterContext);
  if (context === undefined) {
    throw new Error(
      'useCrossPlatformGlobalFilter must be used within a CrossPlatformGlobalFilterProvider',
    );
  }
  return context;
};

export const CrossPlatformGlobalFilterProvider = ({
  initialDeviceFamilyFilters = {},
  initialBlueprintFilters = [],
  children,
}: {
  initialDeviceFamilyFilters?: { [key: string]: DeviceFilter };
  initialBlueprintFilters?: string[];
  children: React.ReactNode;
}) => {
  const { androidEnabled, windowsEnabled } = useGetEnabledPlatforms();

  const defaultDeviceFilters = React.useMemo(() => {
    const initialFilters = {
      apple: {
        id: 'Apple',
        label: i18n.t('Apple'),
        icon: 'apple-logo',
        value: DeviceSelectValues.UNCHECKED,
        children: [
          {
            id: 'Mac',
            label: i18n.t('Mac'),
            icon: 'device-mac',
            value: DeviceSelectValues.UNCHECKED,
            parent: 'Apple',
          },
          {
            id: 'iPhone',
            label: i18n.t('iPhone'),
            icon: 'device-iphone',
            value: DeviceSelectValues.UNCHECKED,
            parent: 'Apple',
          },
          {
            id: 'iPad',
            label: i18n.t('iPad'),
            icon: 'device-ipad',
            value: DeviceSelectValues.UNCHECKED,
            parent: 'Apple',
          },
          {
            id: 'AppleTV',
            label: i18n.t('Apple TV'),
            icon: 'device-apple-tv',
            value: DeviceSelectValues.UNCHECKED,
            parent: 'Apple',
          },
          {
            id: 'Vision',
            label: i18n.t('Vision'),
            icon: 'device-vision',
            value: DeviceSelectValues.UNCHECKED,
            parent: 'Apple',
          },
        ],
      },
    };

    if (windowsEnabled) {
      initialFilters.windows = {
        id: 'Windows',
        label: i18n.t('Windows'),
        icon: 'windows-logo',
        value: DeviceSelectValues.UNCHECKED,
      };
    }

    if (androidEnabled) {
      initialFilters.android = {
        id: 'Android',
        label: i18n.t('Android'),
        icon: 'android-logo',
        value: DeviceSelectValues.UNCHECKED,
      };
    }

    return initialFilters;
  }, [androidEnabled, windowsEnabled]);

  const initialValueRef = React.useRef({
    deviceFamilyFilters: {
      ...defaultDeviceFilters,
      ...initialDeviceFamilyFilters,
    },
    blueprintFilters: initialBlueprintFilters,
  });
  const prismUrl = usePrismUrlContext();
  const [deviceFamilyFilters, setDeviceFamilyFilters] = React.useState(
    initialValueRef.current.deviceFamilyFilters,
  );
  const [blueprintFilters, setBlueprintFilters] = React.useState<string[]>(
    initialValueRef.current.blueprintFilters,
  );

  React.useEffect(() => {
    const value = Array.from(
      new Set(
        Object.values(deviceFamilyFilters)
          .filter(
            (deviceFamily) =>
              deviceFamily.value !== DeviceSelectValues.UNCHECKED,
          )
          .flatMap((deviceFamily) =>
            deviceFamily.children?.length
              ? deviceFamily.children
                  .filter((child) => child.value === DeviceSelectValues.CHECKED)
                  .map((child) => child.id)
              : deviceFamily.id,
          )
          .filter(Boolean),
      ),
    );

    prismUrl.setDeviceFamilies(value);
  }, [deviceFamilyFilters, prismUrl.setDeviceFamilies]);

  React.useEffect(() => {
    prismUrl.setBlueprints(blueprintFilters);
  }, [blueprintFilters, prismUrl.setBlueprints]);

  const contextValue = React.useMemo(
    () => ({
      deviceFamilyFilters,
      setDeviceFamilyFilters: (key: string, filter: DeviceFilter) => {
        setDeviceFamilyFilters((prev) => ({
          ...prev,
          [key]: filter,
        }));
      },
      resetDeviceFamilyFilters: () => {
        const allUnchecked = Object.fromEntries(
          Object.entries(deviceFamilyFilters).map(([key, filter]) => {
            if (filter.children?.length) {
              const children = filter.children.map((child) => ({
                ...child,
                value: DeviceSelectValues.UNCHECKED,
              }));

              return [
                key,
                {
                  ...filter,
                  value: DeviceSelectValues.UNCHECKED,
                  children,
                },
              ];
            }
            return [
              key,
              {
                ...filter,
                value: DeviceSelectValues.UNCHECKED,
              },
            ];
          }),
        );

        setDeviceFamilyFilters(allUnchecked);
      },
      blueprintFilters,
      setBlueprintFilters: (filters: string[]) => {
        setBlueprintFilters(filters);
        prismUrl.setBlueprints(filters);
      },
      resetBlueprintFilters: () => {
        setBlueprintFilters([]);
        prismUrl.resetBlueprints();
      },
    }),
    [
      deviceFamilyFilters,
      blueprintFilters,
      prismUrl.setDeviceFamilies,
      prismUrl.setBlueprints,
    ],
  );

  return (
    <CrossPlatformGlobalFilterContext.Provider value={contextValue}>
      {children}
    </CrossPlatformGlobalFilterContext.Provider>
  );
};
